import React, { memo, useState, useEffect } from "react";
import { Clock, Mail, Phone, Menu, X, ChevronDown, ChevronRight } from "lucide-react";
import { useLocation, Link } from "react-router-dom";
import Logo from "../../assets/Logo.png";

const productDropdownItems = [
  { name: "Slip On Flange", path: "/products/slip-on" },
  { name: "Weld Neck Flange", path: "/products/weld-neck" },
  { name: "Blind Flange", path: "/products/blind" },
  { name: "Long Weld Neck", path: "/products/long-weld" },
  { name: "Threaded Flanges", path: "/products/threaded" },
  { name: "Socket Weld Flange", path: "/products/socket-weld" },
  { name: "Lap Joint Flange", path: "/products/lap-joint" },
  { name: "Companion Flange", path: "/products/plate" },
  { name: "Studding Outlet Flange", path: "/products/rf-rij" },
];

const Navbar = memo(() => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileProductsOpen, setIsMobileProductsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsMobileProductsOpen(false);
  }, [location.pathname]);

  return (
    <header className="sticky top-0 z-50 w-full font-sans bg-white shadow-xs">
      {/* 1. Top Utility Strip */}
      <div className="w-full bg-[#1c1d1f] text-gray-300 text-xs py-2 px-4 sm:px-6 lg:px-8 border-b border-gray-800">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-2">
          <div className="flex items-center gap-2">
            <Clock className="w-3.5 h-3.5 text-amber-500" />
            <span>Thursday - Tuesday: 8:30 AM - 8:00 PM</span>
          </div>
          <div className="flex items-center gap-6">
            <a
              href="mailto:info@auriceng.in"
              className="flex items-center gap-1.5 hover:text-white transition-colors"
            >
              <Mail className="w-3.5 h-3.5 text-amber-500" />
              <span>info@auriceng.in</span>
            </a>
            <a
              href="tel:04023353898"
              className="flex items-center gap-1.5 hover:text-white transition-colors"
            >
              <Phone className="w-3.5 h-3.5 text-amber-500" />
              <span>+91 7434905789</span>
            </a>
          </div>
        </div>
      </div>

      {/* 2. Main Header / Navigation */}
      <nav
        className={`w-full bg-white transition-all duration-300 ${
          isScrolled
            ? "shadow-md border-b border-gray-200"
            : "border-b border-gray-100"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          {/* Logo & Tagline */}
          <Link to="/" className="flex items-center gap-3 group shrink-0">
            <img
              src={Logo}
              alt="Auric Engineering Logo"
              className="h-10 md:h-12 w-auto object-contain max-h-[48px]"
            />
          </Link>

          {/* Desktop & Laptop Navigation */}
          <div className="hidden md:flex items-center space-x-4 lg:space-x-7 text-xs lg:text-sm font-semibold text-gray-700">
            <Link
              to="/"
              className={`hover:text-[#12385a] transition-colors py-2 ${
                location.pathname === "/"
                  ? "text-[#12385a] border-b-2 border-[#12385a]"
                  : ""
              }`}
            >
              Home
            </Link>

            <Link
              to="/aboutus"
              className={`hover:text-[#12385a] transition-colors py-2 ${
                location.pathname === "/aboutus"
                  ? "text-[#12385a] border-b-2 border-[#12385a]"
                  : ""
              }`}
            >
              About Us
            </Link>

            {/* Products Dropdown (Desktop/Laptop) */}
            <div className="relative group py-2">
              <Link
                to="/products"
                className={`inline-flex items-center gap-1 hover:text-[#12385a] transition-colors ${
                  location.pathname.startsWith("/products")
                    ? "text-[#12385a] border-b-2 border-[#12385a]"
                    : ""
                }`}
              >
                <span>Products</span>
                <ChevronDown className="w-4 h-4 transition-transform duration-200 group-hover:rotate-180 text-amber-600" />
              </Link>

              {/* Modern Mega Dropdown Card */}
              <div className="absolute left-0 top-full pt-2 opacity-0 group-hover:opacity-100 invisible group-hover:visible transition-all duration-200 z-50 pointer-events-none group-hover:pointer-events-auto">
                <div className="w-[420px] lg:w-[480px] bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden p-4">
                  <div className="flex items-center justify-between border-b border-gray-100 pb-2 mb-3">
                    <span className="text-xs font-extrabold uppercase tracking-wider text-gray-400">
                      Industrial Flanges & Fittings
                    </span>
                    <Link
                      to="/products"
                      className="text-xs font-bold text-amber-600 hover:text-amber-700 flex items-center gap-1 transition-colors"
                    >
                      <span>View All</span>
                      <ChevronRight className="w-3 h-3" />
                    </Link>
                  </div>

                  <div className="grid grid-cols-2 gap-1.5">
                    {productDropdownItems.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`group/item flex items-center justify-between px-3 py-2 rounded-md text-xs font-semibold transition-all duration-150 ${
                          location.pathname === item.path
                            ? "bg-amber-50 text-amber-700"
                            : "text-gray-700 hover:bg-gray-100 hover:text-[#12385a]"
                        }`}
                      >
                        <span>{item.name}</span>
                        <ChevronRight className="w-3 h-3 text-amber-500 opacity-0 group-hover/item:opacity-100 transition-opacity" />
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <Link
              to="/quality-control"
              className={`hover:text-[#12385a] transition-colors py-2 ${
                location.pathname === "/quality-control"
                  ? "text-[#12385a] border-b-2 border-[#12385a]"
                  : ""
              }`}
            >
              Quality
            </Link>

            <Link
              to="/contact-us"
              className="ml-2 px-3 py-1.5 lg:px-4 lg:py-2 bg-[#12385a] text-white text-xs font-bold rounded uppercase tracking-wider hover:bg-[#0c243b] transition-colors shrink-0"
            >
              Contact Us
            </Link>
          </div>

          {/* Mobile Hamburger Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 text-gray-700 hover:text-[#12385a] focus:outline-none"
            aria-label="Toggle navigation menu"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile Navigation Drawer */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200 px-4 py-4 space-y-3 text-sm font-semibold text-gray-800">
            <Link
              to="/"
              className="block py-1 hover:text-[#12385a]"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Home
            </Link>

            <Link
              to="/aboutus"
              className="block py-1 hover:text-[#12385a]"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              About Us
            </Link>

            {/* Mobile Products Submenu Accordion */}
            <div>
              <div className="flex items-center justify-between py-1">
                <Link
                  to="/products"
                  className="hover:text-[#12385a]"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Products
                </Link>
                <button
                  onClick={() => setIsMobileProductsOpen(!isMobileProductsOpen)}
                  className="p-1 text-gray-500 hover:text-[#12385a]"
                  aria-label="Toggle products menu"
                >
                  <ChevronDown
                    className={`w-4 h-4 transition-transform duration-200 ${isMobileProductsOpen ? "rotate-180" : ""
                      }`}
                  />
                </button>
              </div>

              {isMobileProductsOpen && (
                <div className="ml-3 mt-1 pl-3 border-l-2 border-amber-500 space-y-1.5 py-1 text-xs">
                  {productDropdownItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className="block py-1 text-gray-600 hover:text-[#12385a] transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/manufacture"
              className="block py-1 hover:text-[#12385a]"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Manufacture
            </Link>

            <Link
              to="/quality-control"
              className="block py-1 hover:text-[#12385a]"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Quality
            </Link>

            <Link
              to="/manufacturing-capacity"
              className="block py-1 hover:text-[#12385a]"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Manufacturing Capacity
            </Link>

            <Link
              to="/contact-us"
              className="block text-center py-2.5 bg-[#12385a] text-white text-xs font-bold rounded uppercase tracking-wider hover:bg-[#0c243b] transition-colors mt-4"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Contact Us
            </Link>
          </div>
        )}
      </nav>
    </header>
  );
});

Navbar.displayName = "Navbar";
export default Navbar;